import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Image, Video, Save, X, 
  Layers, ArrowLeft, LogOut, Upload
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEcosystem = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newMediaCaption, setNewMediaCaption] = useState('');
  const [newMediaFiles, setNewMediaFiles] = useState([]);
  const [addingMediaTo, setAddingMediaTo] = useState(null);
  const { authenticated, loading: authLoading, logout, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !authenticated) {
      navigate('/admin/login');
    }
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchSections();
    }
  }, [authenticated]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ecosystem/sections`, {
        headers: getAuthHeader()
      });
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast.error('Failed to load ecosystem sections');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section._id);
    setEditForm({
      title: section.title,
      description: section.description,
      color_scheme: section.color_scheme,
      icon: section.icon
    });
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditForm({});
  };

  const handleSave = async (sectionId) => {
    try {
      await axios.put(`${API}/admin/ecosystem/sections/${sectionId}`, editForm, {
        headers: getAuthHeader()
      });
      toast.success('Section updated successfully');
      setEditingSection(null);
      fetchSections();
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    }
  };

  const handleAddMedia = async (sectionId) => {
    if (newMediaFiles.length === 0) {
      toast.error('Please upload a media file');
      return;
    }

    try {
      // Add each uploaded file as media
      for (const file of newMediaFiles) {
        await axios.post(`${API}/admin/ecosystem/sections/${sectionId}/media`, {
          url: file.url,
          type: file.media_type || 'image',
          caption: newMediaCaption
        }, {
          headers: getAuthHeader()
        });
      }
      toast.success('Media added successfully');
      setNewMediaFiles([]);
      setNewMediaCaption('');
      setAddingMediaTo(null);
      fetchSections();
    } catch (error) {
      console.error('Error adding media:', error);
      toast.error('Failed to add media');
    }
  };

  const handleDeleteMedia = async (sectionId, mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/ecosystem/sections/${sectionId}/media/${mediaId}`, {
        headers: getAuthHeader()
      });
      toast.success('Media deleted successfully');
      fetchSections();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const getColorClass = (scheme) => {
    const colors = {
      green: 'bg-green-50 border-green-200',
      amber: 'bg-amber-50 border-amber-200',
      teal: 'bg-teal-50 border-teal-200',
      blue: 'bg-blue-50 border-blue-200'
    };
    return colors[scheme] || colors.green;
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Ecosystem Management</h1>
          <p className="text-sm text-gray-500">Manage Livestock, Crops, and Trees & Mushrooms sections</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Loading sections...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {sections.map((section) => (
              <Card key={section._id} className={`${getColorClass(section.color_scheme)} border-2`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{section.icon}</span>
                      {editingSection === section._id ? (
                        <Input
                          value={editForm.title}
                          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                          className="text-2xl font-bold w-64"
                        />
                      ) : (
                        <span className="text-2xl">{section.title}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {editingSection === section._id ? (
                        <>
                          <Button size="sm" onClick={() => handleSave(section._id)}>
                            <Save className="h-4 w-4 mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleEdit(section)}>
                          <Pencil className="h-4 w-4 mr-1" /> Edit Details
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Description */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    {editingSection === section._id ? (
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded-md min-h-[100px]"
                      />
                    ) : (
                      <p className="text-gray-700">{section.description}</p>
                    )}
                  </div>

                  {/* Media Gallery */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <Layers className="h-5 w-5" />
                        Media Gallery ({section.media?.length || 0} items)
                      </h4>
                      <Button 
                        size="sm" 
                        onClick={() => setAddingMediaTo(addingMediaTo === section._id ? null : section._id)}
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Media
                      </Button>
                    </div>

                    {/* Add Media Form */}
                    {addingMediaTo === section._id && (
                      <div className="bg-white p-4 rounded-lg border mb-4 space-y-3">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Media *</label>
                            <FileUpload
                              category="ecosystem"
                              files={newMediaFiles}
                              onChange={setNewMediaFiles}
                              multiple={true}
                              allowVideo={true}
                              showPrimary={false}
                              sortable={false}
                              label="Upload Images/Videos"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Caption (Optional)</label>
                            <Input
                              placeholder="Optional caption for all uploaded media"
                              value={newMediaCaption}
                              onChange={(e) => setNewMediaCaption(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleAddMedia(section._id)} disabled={newMediaFiles.length === 0}>
                            <Plus className="h-4 w-4 mr-1" /> Add Media
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setAddingMediaTo(null); setNewMediaFiles([]); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Media Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {section.media?.map((media, index) => (
                        <div key={media.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                            {media.type === 'video' ? (
                              <video
                                src={media.url}
                                className="w-full h-full object-cover"
                                muted
                              />
                            ) : (
                              <img
                                src={media.url}
                                alt={media.caption || `Media ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            {/* Overlay with delete button */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button
                                onClick={() => handleDeleteMedia(section._id, media.id)}
                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                            {/* Type indicator */}
                            <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs flex items-center gap-1">
                              {media.type === 'video' ? <Video className="h-3 w-3" /> : <Image className="h-3 w-3" />}
                              {media.type}
                            </div>
                            {/* Order indicator */}
                            <div className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold shadow">
                              {index + 1}
                            </div>
                          </div>
                          {media.caption && (
                            <p className="mt-1 text-xs text-gray-600 truncate">{media.caption}</p>
                          )}
                        </div>
                      ))}
                      
                      {(!section.media || section.media.length === 0) && (
                        <div className="col-span-full text-center py-8 text-gray-500">
                          No media added yet. Click "Add Media" to add images or videos.
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Tips for Adding Media</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Images:</strong> Use direct image URLs (ending in .jpg, .png, .webp, etc.)</li>
            <li>• <strong>Videos:</strong> Use direct video URLs (ending in .mp4, .webm) or embed URLs</li>
            <li>• <strong>Recommended:</strong> Use high-quality images (at least 800x600 pixels)</li>
            <li>• <strong>Order:</strong> The first image will be the default displayed image</li>
          </ul>
        </div>
    </div>
  );
};

export default AdminEcosystem;
